import { ReactNode, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  BanknotesIcon,
  ChartBarIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import { FinancialDecision, IVehicle } from "../../models/Vehicle";
import { ILead } from "../../models/Lead";
import { IPart } from "../../models/Part";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useLeadMutations from "../../data/useLeadMutations";
import useVehiclePricingMatrix from "../../data/useVehiclePricingMatrix";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import bestColumnTotal from "../../utils/bestColumnTotal";
import useVatMarginScheme from "../../data/useVatMarginScheme";

const MetricRow = ({
  label,
  value,
  bold = false,
  valueClassName,
  hint,
}: {
  label: string;
  value: string;
  bold?: boolean;
  valueClassName?: string;
  hint?: string;
}) => (
  <div className="flex items-start justify-between gap-4 py-2.5">
    <div className="min-w-0">
      <p className={classNames("text-sm", bold ? "font-semibold text-gray-900" : "text-gray-500")}>{label}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-400">{hint}</p>}
    </div>
    <p className={classNames("shrink-0 text-sm tabular-nums text-gray-900", bold && "font-semibold", valueClassName)}>
      {value}
    </p>
  </div>
);

const EditableMetric = ({
  label,
  value,
  onChange,
  onBlur,
  disabled = false,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  disabled?: boolean;
  hint?: string;
}) => (
  <div className="flex items-start justify-between gap-4 py-2.5">
    <div className="min-w-0">
      <p className="text-sm text-gray-500">{label}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-400">{hint}</p>}
    </div>
    <div className="relative w-32 shrink-0">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">£</span>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        placeholder="0.00"
        className="w-full rounded-md border border-gray-300 bg-white pl-6 pr-2.5 py-1.5 text-right text-sm tabular-nums shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-400"
      />
    </div>
  </div>
);

const SectionCard = ({
  title,
  description,
  icon: Icon,
  children,
  footer,
}: {
  title: string;
  description: string;
  icon: typeof BanknotesIcon;
  children: ReactNode;
  footer?: ReactNode;
}) => (
  <section className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    <div className="flex items-start gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3.5">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>
    </div>
    <div className="flex-1 divide-y divide-gray-100 px-4 py-1">{children}</div>
    {footer && <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">{footer}</div>}
  </section>
);

const decisionMeta = (decision: FinancialDecision, t: (key: string) => string) => {
  switch (decision) {
    case FinancialDecision.Target:
      return {
        label: t("Target"),
        badge: "bg-green-100 text-green-800 ring-green-600/20",
        banner: "border-green-200 bg-gradient-to-br from-green-50 to-white",
      };
    case FinancialDecision.Rejected:
      return {
        label: t("Rejected"),
        badge: "bg-red-100 text-red-800 ring-red-600/20",
        banner: "border-red-200 bg-gradient-to-br from-red-50 to-white",
      };
    case FinancialDecision.Purchased:
      return {
        label: t("Purchased"),
        badge: "bg-blue-100 text-blue-800 ring-blue-600/20",
        banner: "border-blue-200 bg-gradient-to-br from-blue-50 to-white",
      };
    default:
      return {
        label: t("No decision"),
        badge: "bg-gray-100 text-gray-700 ring-gray-500/20",
        banner: "border-gray-200 bg-gradient-to-br from-gray-50 to-white",
      };
  }
};

export default function VehicleFinancialSummary({ vehicle, lead = null }: { vehicle: IVehicle; lead?: ILead | null }) {
  const { t } = useTranslation("vehicles");
  const { patchVehicle } = useVehiclesMutations();
  const { update: updateLead } = useLeadMutations(lead?.id ?? "", "VehicleFinancialSummary");
  const { vehiclePricingMatrix } = useVehiclePricingMatrix(vehicle.id, "VehicleFinancialSummary");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Financial Decision"));
  const { saveHandlers: priceHandlers } = useDefaultCRUDHandlers(t("Target Price"));
  const { saveHandlers: leadPriceHandlers } = useDefaultCRUDHandlers(t("Vehicle Price"));
  const queryClient = useQueryClient();
  const { standardRate } = useVatMarginScheme();

  // Editable prices are held as local strings so the inputs stay responsive,
  // re-synced whenever the underlying record changes (e.g. after a save refetch).
  const [currentPriceInput, setCurrentPriceInput] = useState("");
  const [targetPriceInput, setTargetPriceInput] = useState("");

  useEffect(() => {
    setCurrentPriceInput(lead?.priceWithVAT != null ? String(lead.priceWithVAT) : "");
  }, [lead?.priceWithVAT]);

  useEffect(() => {
    setTargetPriceInput(vehicle.targetPrice != null ? String(vehicle.targetPrice) : "");
  }, [vehicle.targetPrice]);

  const parts = vehicle.parts ?? [];
  const partPrice = (part: IPart) => part.price ?? part.enteredPrice ?? 0;
  const coreValue = parts.filter((part) => part.isCore).reduce((sum, part) => sum + partPrice(part), 0);
  const partsValue = parts.filter((part) => !part.isCore).reduce((sum, part) => sum + partPrice(part), 0);
  const total = partsValue + coreValue;

  const potentialValue = bestColumnTotal(vehiclePricingMatrix.data?.data);

  // "Current price" is the lead's VAT-inclusive price; profit is the dismantling
  // total less what we pay for the vehicle.
  const currentPrice = currentPriceInput.trim() === "" ? 0 : Number(currentPriceInput);
  const safeCurrentPrice = Number.isNaN(currentPrice) ? 0 : currentPrice;
  const profit = total - safeCurrentPrice;
  const potentialProfit = potentialValue - safeCurrentPrice;

  const decision = vehicle.financialDecision ?? FinancialDecision.None;
  const meta = decisionMeta(decision, t);

  const setDecision = (next: FinancialDecision) => {
    const value = decision === next ? FinancialDecision.None : next;

    queryClient.setQueriesData(["pagedTable"], (oldData: any) => {
      if (!oldData?.data?.rows) return oldData;
      return {
        ...oldData,
        data: {
          ...oldData.data,
          rows: oldData.data.rows.map((row: any) =>
            row.id === vehicle.id ? { ...row, financialDecision: value } : row
          ),
        },
      };
    });

    patchVehicle.mutate(
      { vehicleId: vehicle.id, patch: { financialDecision: value } },
      {
        ...saveHandlers,
        onSettled: () => queryClient.invalidateQueries(["pagedTable"]),
      }
    );
  };

  const handleCurrentPriceBlur = () => {
    if (!lead) return;

    const trimmed = currentPriceInput.trim();
    const parsed = trimmed === "" ? 0 : Number(trimmed);

    if (Number.isNaN(parsed)) {
      setCurrentPriceInput(lead.priceWithVAT != null ? String(lead.priceWithVAT) : "");
      return;
    }

    // The input shows the VAT-inclusive price; the lead stores the net price.
    const basePrice = lead.applyVAT ? parsed / (1 + standardRate / 100) : parsed;
    if (basePrice === lead.price) return;

    updateLead.mutate({ ...lead, price: basePrice }, leadPriceHandlers);
  };

  const handleTargetPriceBlur = () => {
    const trimmed = targetPriceInput.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);

    if (parsed != null && Number.isNaN(parsed)) {
      setTargetPriceInput(vehicle.targetPrice != null ? String(vehicle.targetPrice) : "");
      return;
    }

    if (parsed === (vehicle.targetPrice ?? null)) return;

    patchVehicle.mutate({ vehicleId: vehicle.id, patch: { targetPrice: parsed } }, priceHandlers);
  };

  const decisionButtons: { value: FinancialDecision; label: string; active: string; idle: string }[] = [
    {
      value: FinancialDecision.Target,
      label: t("Target"),
      active: "border-transparent bg-green-600 text-white hover:bg-green-700",
      idle: "border-green-500 text-green-700 hover:bg-green-50",
    },
    {
      value: FinancialDecision.Rejected,
      label: t("Rejected"),
      active: "border-transparent bg-red-600 text-white hover:bg-red-700",
      idle: "border-red-500 text-red-700 hover:bg-red-50",
    },
    {
      value: FinancialDecision.Purchased,
      label: t("Purchased"),
      active: "border-transparent bg-blue-600 text-white hover:bg-blue-700",
      idle: "border-blue-500 text-blue-700 hover:bg-blue-50",
    },
  ];

  return (
    <div className="space-y-5">
      <div className={classNames("rounded-xl border p-5 shadow-sm", meta.banner)}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900">{t("Financial Summary")}</h2>
              <span className={classNames("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset", meta.badge)}>
                {meta.label}
              </span>
            </div>
            <p className="mt-1 max-w-xl text-sm text-gray-500">
              {t("Review purchase price, parts value and opportunity, then set the financial decision for this vehicle.")}
            </p>
          </div>

          <div className="flex w-full gap-2 sm:w-auto sm:min-w-[20rem]">
            {decisionButtons.map((btn) => (
              <button
                key={btn.value}
                type="button"
                onClick={() => setDecision(btn.value)}
                disabled={patchVehicle.isLoading}
                className={classNames(
                  "flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60",
                  decision === btn.value ? btn.active : btn.idle
                )}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <SectionCard
          title={t("Acquisition")}
          description={t("What we pay / target for the vehicle")}
          icon={BanknotesIcon}
        >
          <EditableMetric
            label={t("Current Price")}
            hint={t("VAT-inclusive")}
            value={currentPriceInput}
            onChange={setCurrentPriceInput}
            onBlur={handleCurrentPriceBlur}
            disabled={!lead || updateLead.isLoading}
          />
          <EditableMetric
            label={t("Target Price")}
            hint={t("Internal buying target")}
            value={targetPriceInput}
            onChange={setTargetPriceInput}
            onBlur={handleTargetPriceBlur}
            disabled={patchVehicle.isLoading}
          />
          <MetricRow
            label={t("Reserved Price")}
            value={vehicle.reservedPrice != null ? PriceFormat(vehicle.reservedPrice) : "—"}
          />
        </SectionCard>

        <SectionCard
          title={t("Opportunity")}
          description={t("Best-case value from the pricing matrix")}
          icon={ChartBarIcon}
          footer={
            <MetricRow
              label={t("Potential Profit")}
              value={PriceFormat(potentialProfit)}
              bold
              valueClassName={potentialProfit < 0 ? "text-red-600" : "text-green-700"}
            />
          }
        >
          <MetricRow label={t("Potential Value")} value={PriceFormat(potentialValue)} />
        </SectionCard>

        <SectionCard
          title={t("Parts Value")}
          description={t("Value from listed / core parts on this vehicle")}
          icon={CubeIcon}
          footer={
            <div className="space-y-0 divide-y divide-gray-200">
              <MetricRow label={t("Total")} value={PriceFormat(total)} bold />
              <MetricRow
                label={t("Profit")}
                value={PriceFormat(profit)}
                bold
                valueClassName={profit < 0 ? "text-red-600" : "text-green-700"}
              />
            </div>
          }
        >
          <MetricRow label={t("Parts Value")} value={PriceFormat(partsValue)} />
          <MetricRow label={t("Core Value")} value={PriceFormat(coreValue)} />
          <MetricRow label={t("Scrap Price")} value="—" hint={t("Coming soon")} />
        </SectionCard>
      </div>
    </div>
  );
}
